import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Lock, Eye, EyeSlash, CheckCircle, Warning } from '@phosphor-icons/react';
import BrandLogo from '../components/BrandLogo';

export default function ResetPasswordPage() {
  const { API } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setValidating(false); return; }
    axios.get(`${API}/auth/validate-reset-token`, { params: { token } })
      .then(() => setTokenValid(true))
      .catch(err => setError(err.response?.data?.detail || 'Invalid or expired reset link'))
      .finally(() => setValidating(false));
  }, [token, API]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2200);
    } catch (err) { setError(err.response?.data?.detail || 'Failed to reset password'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <BrandLogo size="lg" />
          <span className="text-lg font-semibold text-zinc-900 tracking-tight">Inspiro Projects</span>
        </div>
        <Card className="border-0 shadow-xl shadow-indigo-100/60 bg-white">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl font-semibold tracking-tight">{done ? 'Password reset!' : 'Choose a new password'}</CardTitle>
            <CardDescription className="text-zinc-500 text-sm">
              {done ? 'Redirecting you to login…' : 'Enter a new password for your account.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {validating ? (
              <div className="text-center text-sm text-zinc-400 py-6">Validating your reset link…</div>
            ) : !tokenValid ? (
              <div className="text-center py-6">
                <Warning weight="fill" className="w-10 h-10 text-red-500 mx-auto mb-3" />
                <p className="text-sm text-zinc-600 mb-4">{error || 'This reset link is invalid or has expired.'}</p>
                <Link to="/forgot-password"><Button>Request a new link</Button></Link>
              </div>
            ) : done ? (
              <div className="text-center py-6">
                <CheckCircle weight="fill" className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-sm text-zinc-600">You can now log in with your new password.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium">New password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Minimum 6 characters" className="pl-10 pr-10 h-10" required data-testid="reset-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" tabIndex={-1} data-testid="toggle-reset-password">
                      {showPassword ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium">Confirm new password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" className="pl-10 h-10" required data-testid="reset-confirm-input" />
                  </div>
                </div>
                {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded p-2">{error}</p>}
                <Button type="submit" className="w-full h-10 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-medium" disabled={loading} data-testid="reset-submit-btn">
                  {loading ? 'Resetting…' : 'Reset password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
